import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { toast } from "../components/ui/use-toast";

/**
 * Custom hook for fetching and caching churn data
 */
export default function useChurnData() {
  const [churnData, setChurnData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchedCodes, setLastFetchedCodes] = useState([]);

  // Function to fetch churn data for a list of customer codes
  const fetchChurnData = useCallback(
    async (customerCodes) => {
      // Skip if no customer codes provided
      if (!customerCodes || customerCodes.length === 0) {
        console.log("No customer codes provided, skipping churn data fetch");
        return { data: [], error: null };
      }

      console.log(
        `Fetching churn data for ${customerCodes.length} customers:`,
        customerCodes,
      );

      // Skip if these exact codes were just fetched
      if (JSON.stringify(customerCodes) === JSON.stringify(lastFetchedCodes)) {
        console.log("Using cached churn data for the same customer codes");
        return { data: Object.values(churnData), error: null };
      }

      setLoading(true);
      setError(null);

      try {
        console.log("Making API call to /churn/filtered with payload:", {
          customer_codes: customerCodes,
        });
        const API_BASE_URL = process.env.REACT_APP_API_URL || "https://isco-backend-deployment-932661235642.us-central1.run.app";
        const response = await axios.post(
          `${API_BASE_URL}/churn/filtered`,
          {
            customer_codes: customerCodes,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          },
        );

        console.log("Churn API response:", response.data);

        // Process and cache the data
        const newData = { ...churnData };

        if (response.data && Array.isArray(response.data)) {
          console.log(`Processing ${response.data.length} churn data records`);

          response.data.forEach((item) => {
            if (item.CustomerCode) {
              console.log(
                `Caching churn data for CustomerCode: ${item.CustomerCode}`,
              );
              newData[item.CustomerCode] = item;
            } else {
              console.warn(
                `Received churn data item without CustomerCode:`,
                item,
              );
            }
          });

          console.log("Updated churn data cache:", newData);

          // Test: check if we have data for the example customer code "108260"
          if (newData["108260"]) {
            console.log(
              "TEST PASSED: Successfully cached data for customer 108260:",
              newData["108260"],
            );
          } else {
            console.log("TEST FAILED: No data for customer 108260 in cache");
          }

          setChurnData(newData);
          setLastFetchedCodes(customerCodes);

          return { data: response.data, error: null };
        }

        console.warn("Invalid response format from churn API");
        return { data: [], error: "Invalid response format" };
      } catch (err) {
        console.error("Error fetching churn data:", err);
        const errorMessage =
          err.response?.data?.error ||
          err.message ||
          "Failed to fetch churn data";
        setError(errorMessage);

        // Don't show toast for 404s (expected when no data available)
        if (!err.response || err.response.status !== 404) {
          toast({
            title: "Error",
            description: errorMessage,
            variant: "destructive",
          });
        }

        return { data: [], error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    [churnData, lastFetchedCodes],
  );

  // Function to clear cache when filters change
  const clearChurnCache = useCallback(() => {
    console.log("Clearing churn data cache");
    setChurnData({});
    setLastFetchedCodes([]);
  }, []);

  // Function to get churn data for a specific customer code
  const getChurnDataForCustomer = useCallback(
    (customerCode) => {
      if (!customerCode) {
        console.log("No customer code provided to getChurnDataForCustomer");
        return null;
      }

      // Ensure we're using string comparison since CustomerCode in the API response is a string
      const stringCode = String(customerCode);
      const data = churnData[stringCode];

      console.log(`Getting churn data for customer ${stringCode}:`, data);
      return data || null;
    },
    [churnData],
  );

  return {
    churnData,
    loading,
    error,
    fetchChurnData,
    clearChurnCache,
    getChurnDataForCustomer,
  };
}
